import { ChangeEvent, useState } from "react";
import { useParams } from "react-router-dom";

import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { Add, Visibility } from "@mui/icons-material";

import { CreateArtefacts } from "../component/CreateArtefacts";
import { ArtefactsDetails } from "../component/ArtefactsDetails";

import { useFetchArtefacts } from "../hooks/useFetchArtefacts";
import { useFetchArtefactsState } from "../hooks/useFetchArtefactsState";

import { BudgetCard, DrawerControl, MetaDrivenTable, PaginationControls, SearchField } from "../../../shared/components";
import { headers, metrics } from "../utils";
import { useCapabilities } from "../../../hooks/useCapabilities";

export const Documents: React.FC = () => {

    const { can } = useCapabilities();

    const isAllowedAddArtefact = can("operational_delivery:project:artefact:add");

    const { projectId } = useParams();

    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');

    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(25);

    const { isPending, artefactsData, artefactsPagination, refetch: refetchArtefacts } = useFetchArtefacts(projectId, page, limit, searchKey);
    const { isPending: isCountPending, artefactsDataState } = useFetchArtefactsState(projectId);

    const [formStatus, setFormStatus] = useState<{
        open: boolean;
        status: boolean;
        id: null | string;
    }>({
        open: false,
        status: false,
        id: null
    });

    const [viewArtefact, setViewArtefact] = useState<{
        status: boolean;
        id: null | string;
        details: { title: string; description: string } | null
    }>({
        status: false,
        id: null,
        details: null
    });

    const changePage = (page: number) => {
        setPage(page);
    };

    const changeRowsPerPage = (limit: number) => {
        setLimit(limit);
    };

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
    };

    const handleClear = () => {
        setSearchDraft('');
        setSearchKey('');
    };

    const handleSearch = () => {
        setSearchKey(searchDraft);
    };

    const onOpen = (status: boolean, id?: string) => {
        setFormStatus({
            open: true,
            status: status,
            id: id ? id : null
        });
    };

    const onClose = () => {
        setFormStatus({
            open: false,
            status: false,
            id: null
        });
    };

    const onOpenArtefactDetails = async (artefact: { id: string, title: string, description: string }) => {
        setViewArtefact({
            status: true,
            id: artefact.id,
            details: {
                title: artefact.title,
                description: artefact.description
            }
        });
    };

    const onCloseArtefactDetails = async () => {
        setViewArtefact({
            status: false,
            id: null,
            details: null
        });
    };

    if (isPending || isCountPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box py={2} display={'flex'} gap={2} flexDirection={'column'} height={'100%'} overflow={'hidden'}>

            <Grid container spacing={2} px={2}>
                {
                    [
                        { label: 'Total Artefacts', value: artefactsDataState.total, icon: 'Balance', color: 'info' as const },
                        { label: 'Approved', value: artefactsDataState.approved, icon: 'Cost', color: 'green' as const },
                        { label: 'Required', value: artefactsDataState.required, icon: 'Assignment', color: 'amber' as const },
                        { label: 'Expired', value: artefactsDataState.optional, icon: 'Trending', color: 'red' as const },
                    ].map((value) => (
                        <Grid size={3}>
                            <BudgetCard label={value.label} value={value.value} icon={value.icon} color={value.color} removeMoney={true} />
                        </Grid>
                    ))
                }
            </Grid>

            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2} px={2}>
                <Box mr={'auto'}>
                    <SearchField searchTerm={searchDraft} handleSearch={handleChangeSearch} handleClearSearch={handleClear} handleApplySearch={handleSearch} />
                </Box>
                {
                    isAllowedAddArtefact && (
                        <Button
                            startIcon={<Add />}
                            variant="text"
                            color="inherit"
                            sx={{ color: 'error.main' }}
                            onClick={() => { onOpen(false) }}
                        >
                            Create Artefact Requirement
                        </Button>
                    )
                }
            </Box>

            <MetaDrivenTable
                headers={headers}
                data={artefactsData}
                dataMetrics={metrics}
                sortDisabled={true}
                actions={[
                    {
                        label: <Visibility />,
                        handleClick: (row: { id: string, title: string, description: string }) => {
                            onOpenArtefactDetails(row);
                        }
                    }]}
            />

            <Box flexShrink={0}>
                <PaginationControls
                    total={artefactsPagination?.total}
                    rowsPerPage={artefactsPagination?.limit}
                    currentPage={artefactsPagination?.page}
                    handlePageChange={changePage}
                    handleRowsPerPage={changeRowsPerPage}
                />
            </Box>

            <DrawerControl
                title={viewArtefact.details?.title || ""}
                subTitle={viewArtefact.details?.description || ""}
                isOpen={viewArtefact.status}
                handleClose={onCloseArtefactDetails}
                children={
                    <ArtefactsDetails
                        projectId={projectId}
                        artefactId={viewArtefact.id}
                        refetchArtefacts={refetchArtefacts}
                    />
                }
            />

            <DrawerControl
                title={"Create/Edit Artefact Requirement"}
                subTitle={""}
                isOpen={formStatus.open}
                handleClose={onClose}
                children={
                    <CreateArtefacts
                        projectId={String(projectId)}
                        isEdit={formStatus.status}
                        artefactId={formStatus.id}
                        onClose={onClose}
                        onSave={() => {
                            onClose();
                            refetchArtefacts();
                        }}
                    />
                }
            />

        </Box>
    )
};